import type { OrderSupplyResponse } from '../order.service';
import { deliveryDeadline, DeliveryHint } from './delivery-deadline.util';

export interface ProductDeliveryReference {
  orderProductId?: number;
  productId?: number;
  quan: number;
  shipped_quan?: number;
  waiting_supply_quan?: number;
  is_ready?: boolean;
}

export type ProductDeliveryHint = DeliveryHint;

export function productDeliveryHint(
  product: ProductDeliveryReference,
  supply?: OrderSupplyResponse,
  now = Date.now()
): ProductDeliveryHint {
  const hint = (label: string, title: string, tone: ProductDeliveryHint['tone'], icon: string) =>
    ({ label, title, tone, icon });
  if (product.quan > 0 && Number(product.shipped_quan || 0) >= product.quan) {
    return hint('Отгружено', 'Позиция полностью отгружена клиенту', 'muted', 'done_all');
  }
  // A SKU can occur on multiple order lines with different suppliers and dates.
  const candidates = supply?.lines.filter((line) => Number(line.product_id) === product.productId) || [];
  const line = product.orderProductId
    ? supply?.lines.find((item) => Number(item.order_product_id) === product.orderProductId)
    : candidates.length === 1 ? candidates[0] : undefined;
  if (product.is_ready || product.waiting_supply_quan === 0) {
    return hint('На складе', 'Поставка по этой позиции не ожидается', 'ready', 'inventory_2');
  }
  if (!line) {
    return hint('Срок не указан', 'Нет данных о сроке поставки этой позиции', 'muted', 'help_outline');
  }

  const entries: Array<ProductDeliveryHint & { timestamp: number; quantity: number }> = [];
  for (const allocation of line.allocations || []) {
    if (allocation.status === 'CANCELLED' || allocation.status === 'RECEIVED') continue;
    const quantity = Math.max(Number(allocation.quantity) - Number(allocation.received_quantity), 0);
    if (!quantity) continue;
    const deadline = deliveryDeadline(allocation.expected_at, now, allocation.expected_at_precision);
    const date = deadline.date;
    const planned = allocation.status === 'PLANNED';
    const overdue = !planned && deadline.tone === 'overdue';
    const label = date ? `${planned ? 'План' : deadline.label} · ${date.short}` : planned ? 'Не заказано' : 'Срок не указан';
    const status = planned ? 'План, ещё не заказано' : date ? deadline.label : 'Ожидается';
    const documents = allocation.buy_ids?.length ? ` · закупка ${allocation.buy_ids.map(id => `№${id}`).join(', ')}` : '';
    entries.push({
      label, quantity, timestamp: date?.timestamp ?? Infinity,
      title: `${allocation.provider || 'Поставщик'}${documents}: ${quantity} шт. · ${status}${date ? ` ${date.full}` : ', срок не указан'}`,
      tone: planned && deadline.tone === 'overdue' ? 'pending' : deadline.tone,
      icon: overdue ? 'schedule' : planned ? 'event' : deadline.icon
    });
  }
  for (const transfer of line.transfer_sources || []) {
    if (transfer.status === 'CANCELLED') continue;
    const quantity = Math.max(Number(transfer.quantity) - Number(transfer.received_quantity), 0);
    if (!quantity) continue;
    const inTransit = ['IN_TRANSIT', 'PARTIALLY_RECEIVED'].includes(transfer.status);
    entries.push({
      label: inTransit ? 'В пути · без срока' : 'Перемещение · без срока',
      title: `Перемещение №${transfer.moving_doc_id} · ${transfer.source_warehouse} → ${transfer.target_warehouse}: ${quantity} шт. · ${inTransit ? 'В пути' : 'Ожидает отправки'}, срок не указан`,
      tone: 'pending', icon: 'local_shipping', quantity, timestamp: Infinity
    });
  }
  if (!line.transfer_sources?.length && Number(line.transfer_pending) > 0) {
    entries.push({ ...hint('Перемещение · без срока', `Ожидается перемещение: ${line.transfer_pending} шт., срок не указан`, 'pending', 'local_shipping'), quantity: Number(line.transfer_pending), timestamp: Infinity });
  }
  if (Number(line.uncovered) > 0) {
    entries.push({ ...hint('Не заказано', `Без источника поставки: ${line.uncovered} шт.`, 'muted', 'help_outline'), quantity: Number(line.uncovered), timestamp: Infinity });
  }
  if (entries.length) {
    entries.sort((left, right) =>
      Number(right.tone === 'overdue') - Number(left.tone === 'overdue') || left.timestamp - right.timestamp);
    const first = entries[0];
    const quantity = entries.length > 1 || first.quantity > 1 ? ` · ${first.quantity} шт.` : '';
    const more = entries.length > 1 ? ` +${entries.length - 1}` : '';
    const received = Number(line.received) > 0 ? `\nУже получено от поставщиков: ${line.received} шт.` : '';
    return { ...first, label: `${first.label}${quantity}${more}`, title: entries.map(item => item.title).join('\n') + received };
  }
  if (Number(line.reserved) > 0 || product.is_ready || product.waiting_supply_quan === 0) {
    return hint('На складе', `Для заказа на складе: ${line.reserved} шт. Поставка не ожидается.`, 'ready', 'inventory_2');
  }
  return Number(line.received) > 0
    ? hint('Получено', `От поставщиков получено: ${line.received} шт.`, 'ready', 'done')
    : hint('Срок не указан', 'Нет подтверждённого срока поставки этой позиции', 'muted', 'help_outline');
}
